import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { safeRedirect } from '@/lib/redirect';
import { authService } from '@/services/auth.service';
import { clubMembershipService } from '@/services/club-membership.service';
import { useAuthStore } from '@/stores/auth.store';
import type { RegisterRequest, LoginRequest, User } from '@/types/auth';
import { toast } from 'sonner';

/**
 * Hook pour récupérer les utilisateurs actifs (suggestions)
 */
export const useActiveUsers = (limit: number = 5) => {
  return useQuery({
    queryKey: ['activeUsers', limit],
    queryFn: () => authService.getActiveUsers(limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5,
  });
};

/**
 * Hook pour l'inscription
 */
export const useRegister = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (response) => {
      setAuth(response.data.user, response.data.tokens);
      toast.success('Compte créé avec succès ! Bienvenue sur FCONNECT 🎉');
      navigate('/dashboard');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || "Erreur lors de l'inscription";
      toast.error(message);
    },
  });
};

/**
 * Hook pour la connexion
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);
  // Venu d'une page précise (le panier, par exemple) : on l'y ramène.
  const redirect = safeRedirect(new URLSearchParams(location.search).get('redirect'));

  return useMutation({
    mutationFn: (data: LoginRequest | { phone: string; code: string }) =>
      'phone' in data ? authService.loginWithPhone(data) : authService.login(data),
    onSuccess: async (response) => {
      setAuth(response.data.user, response.data.tokens);
      toast.success('Connexion réussie ! Bienvenue 👋');
      if (redirect) {
        navigate(redirect);
        return;
      }
      if (response.data.user.user_type !== 'amateur') {
        navigate('/dashboard');
        return;
      }
      // Un vendeur ou un contrôleur de billetterie (compte téléphone + PIN créé
      // par son club) va droit à son poste ; un supporter, lui, vient pour le fil.
      try {
        const { data } = await clubMembershipService.getMyMembership();
        const roles = data.roles ?? [data.role];
        if (roles.some((role) => role === 'ticket_seller' || role === 'ticket_controller')) {
          navigate('/club/billetterie');
          return;
        }
      } catch {
        // Pas de club : un supporter ordinaire.
      }
      navigate('/feed');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Identifiants incorrects';
      toast.error(message);
    },
  });
};

/**
 * Hook pour la déconnexion
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      logout();
      queryClient.clear();
      toast.success('Déconnexion réussie');
      navigate('/login');
    },
    onError: () => {
      // Même en cas d'erreur, déconnecter localement
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });
};

/**
 * Hook pour récupérer l'utilisateur courant
 */
export function useCurrentUser() {
  const { isAuthenticated, updateUser, clearAuth } = useAuthStore();

  const query = useQuery<User>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await authService.getCurrentUser();
      return response.data.user;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // React Query v5: Use useEffect instead of onSuccess/onError
  useEffect(() => {
    if (query.data) {
      updateUser(query.data);
    }
  }, [query.data, updateUser]);

  useEffect(() => {
    // Seul un refus du jeton (401, 403) met fin à la session. Pendant un
    // déploiement, l'API répond 502/503 quelques instants : vider la session à
    // ce moment déconnectait tous les utilisateurs à chaque mise en production.
    const statut = (query.error as { response?: { status?: number } } | null)?.response?.status;
    if (statut === 401 || statut === 403) {
      clearAuth();
    }
  }, [query.error, clearAuth]);

  return query;
}

/**
 * Hook pour mot de passe oublié
 */
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
    onSuccess: (response) => {
      toast.success(response.message);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || "Erreur lors de l'envoi de l'email";
      toast.error(message);
    },
  });
};

/**
 * Hook pour réinitialiser le mot de passe
 */
export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authService.resetPassword(token, password),
    onSuccess: (response) => {
      toast.success(response.message);
      navigate('/login');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la réinitialisation';
      toast.error(message);
    },
  });
};

/**
 * Hook pour vérifier l'email
 */
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: (token: string) => authService.verifyEmail(token),
    onSuccess: (response) => {
      toast.success(response.message);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la vérification';
      toast.error(message);
    },
  });
};
